import React from 'react';

import BezierEasing from 'bezier-easing';

import {TPlacementY} from './types';

export const getVerticalPosition = (
  head: HTMLElement | null,
  placementY: TPlacementY,
  offsetY?: number,
) => {
  if (head === null) {
    return {};
  }
  const headHeight = head.offsetHeight;
  const shift = `${headHeight + (offsetY ?? 0)}px`;

  if (placementY === 'top') {
    return {bottom: shift};
  }

  return {top: shift};
};

export const easing = BezierEasing(0.1, 1, 0.65, 1);

export type UseMeasureRect = Pick<
  DOMRectReadOnly,
  'x' | 'y' | 'top' | 'left' | 'right' | 'bottom' | 'height' | 'width'
>;
export type TUseMeasureRef<E extends Element = Element> = (element: E) => void;
export type TUseMeasureResult<E extends Element = Element> = [TUseMeasureRef<E>, UseMeasureRect];

export const useMeasure = <E extends Element = Element>(): TUseMeasureResult<E> => {
  const [element, ref] = React.useState<E | null>(null);
  const [rect, setRect] = React.useState<UseMeasureRect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  const animationFrameRef = React.useRef<number>();

  const observer = React.useMemo(
    () =>
      new ResizeObserver(([entry]: ResizeObserverEntry[]) => {
        animationFrameRef.current = requestAnimationFrame(() => {
          if (entry) {
            const {x, y, width, height, top, left, bottom, right} = entry.contentRect;

            setRect({x, y, width, height, top, left, bottom, right});
          }
        });
      }),
    [],
  );

  React.useLayoutEffect(() => {
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      observer.disconnect();
    };
  }, [element, observer]);

  return [ref, rect];
};
